/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { AclApiService } from './api/acl'
import {
  FileAclLevel,
  SystemHelperType,
  type FileSystemInfo,
  type AclAssignment,
  type AclUpdateRequest,
  type AclUpdateOptions,
  type UserGroupEntity,
  type AclComparisonResult,
  type AclValidationResult
} from '../types/acl'
import { usersApiService } from './api'

/**
 * ACL Service - React TypeScript equivalent of AngularJS acl.service.js
 *
 * Provides file Access Control List (ACL) management functionality with support for:
 * - ACL level comparison and validation
 * - File permission updates with user and group assignments
 * - Retrieving and processing ACL data for files
 * - Recursive ACL application
 */
export class AclService {
  private static instance: AclService
  private aclLevels: FileAclLevel[]

  private constructor() {
    // Define ACL levels in order of permission hierarchy
    this.aclLevels = [
      FileAclLevel.NONE,
      FileAclLevel.BROWSE,
      FileAclLevel.VIEW,
      FileAclLevel.READ,
      FileAclLevel.WRITE,
      FileAclLevel.DELETE
    ]
  }

  public static getInstance(): AclService {
    if (!AclService.instance) {
      AclService.instance = new AclService()
    }
    return AclService.instance
  }

  /**
   * Check if an ACL level is above or equal to a minimum level
   * @param acl - The ACL level to check
   * @param minLevel - The minimum required ACL level
   * @returns True if acl is above or equal to minLevel
   */
  public isAbove(acl: FileAclLevel, minLevel: FileAclLevel): boolean {
    if (!acl || !minLevel) {
      return false
    }

    const minIndex = this.aclLevels.indexOf(minLevel)
    const aclIndex = this.aclLevels.indexOf(acl)

    return aclIndex >= minIndex
  }

  /**
   * Get detailed comparison result between ACL levels
   * @param acl - The ACL level to check
   * @param minLevel - The minimum required ACL level
   * @returns Detailed comparison result
   */
  public compareAcls(acl: FileAclLevel, minLevel: FileAclLevel): AclComparisonResult {
    const minIndex = this.aclLevels.indexOf(minLevel)
    const aclIndex = this.aclLevels.indexOf(acl)

    return {
      isAbove: aclIndex >= minIndex,
      aclIndex,
      minIndex,
      acl,
      minLevel
    }
  }

  /**
   * Get all available ACL levels in order
   */
  public getAvailableLevels(): FileAclLevel[] {
    return [...this.aclLevels]
  }

  /**
   * Update file ACLs with user and group assignments
   * @param acls - Array of ACL assignments
   * @param fsiId - File system ID
   * @param options - Update options (recursively, onlyChanges)
   * @returns Promise that resolves when update is complete
   */
  public async updateFsiAcls(acls: AclAssignment[], fsiId: number, options: AclUpdateOptions = {}): Promise<void> {
    const { recursively = false, onlyChanges = false } = options

    // Validate ACLs before processing
    const validation = this.validateAcls(acls)
    if (!validation.isValid) {
      throw new Error(`Invalid ACL configuration: ${validation.errors.join(', ')}`)
    }

    const processedAcls: AclUpdateRequest = {
      applyOnlyChanges: onlyChanges,
      applyRecursively: recursively,
      groupAcls: {} as Record<FileAclLevel, number[]>,
      userAcls: {} as Record<FileAclLevel, number[]>
    }

    // Initialize empty arrays for all ACL levels
    this.aclLevels.forEach((level) => {
      processedAcls.groupAcls[level] = []
      processedAcls.userAcls[level] = []
    })

    // Process each ACL assignment
    for (const acl of acls) {
      if (!acl.merged || acl.merged.length === 0) {
        // Skip empty ACLs (commented out the error from original code)
        continue
      }

      // Validate ACL level exists
      if (!this.aclLevels.includes(acl.level)) {
        throw new Error(`Invalid ACL level: ${acl.level}`)
      }

      // Process each user/group in the merged array
      for (const userGroup of acl.merged) {
        if (userGroup.systemHelperType === SystemHelperType.USER) {
          processedAcls.userAcls[acl.level].push(userGroup.id)
        } else if (userGroup.systemHelperType === SystemHelperType.GROUP) {
          processedAcls.groupAcls[acl.level].push(userGroup.id)
        }
      }
    }

    try {
      await AclApiService.updateFileACL(fsiId, processedAcls)
    } catch (error) {
      console.log('Error updating ACLs:', error)
      throw error
    }
  }

  /**
   * Get ACL information for a file
   * @param fsi - File system information
   * @returns Promise that resolves to array of ACL assignments
   */
  public async getFsiAcls(fsi: FileSystemInfo): Promise<AclAssignment[]> {
    if (!fsi || !fsi.id) {
      throw new Error('FSI ID must be specified')
    }

    try {
      // Get available users and groups
      const availableUsersGroupsResponse = await usersApiService.getAssembledUsersGroups(true)
      const availableUsersGroups = availableUsersGroupsResponse.merged

      // Initialize ACL structure
      const acls: Record<FileAclLevel, AclAssignment> = {} as Record<FileAclLevel, AclAssignment>

      this.aclLevels.forEach((level) => {
        acls[level] = {
          level,
          merged: [],
          users: [],
          groups: [],
          availableUsersGroups: [...availableUsersGroups] as any,
          editing: false
        }
      })

      // Get file ACL data
      const fileAclData = await AclApiService.getFileACL(fsi.id)

      // Process user ACLs
      if (fileAclData.userAcls) {
        Object.entries(fileAclData.userAcls).forEach(([level, userIds]) => {
          const aclLevel = level as FileAclLevel
          if (acls[aclLevel] && userIds) {
            userIds.forEach((userId) => {
              const userEntity = availableUsersGroups.find(
                (ug: any) => ug.id === parseInt(userId) && ug.systemHelperType === SystemHelperType.USER
              )
              if (userEntity) {
                acls[aclLevel].merged.push(userEntity as any)
                acls[aclLevel].users.push(userEntity as any)
              }
            })
          }
        })
      }

      // Process group ACLs
      if (fileAclData.groupAcls) {
        Object.entries(fileAclData.groupAcls).forEach(([level, groupIds]) => {
          const aclLevel = level as FileAclLevel
          if (acls[aclLevel] && groupIds) {
            groupIds.forEach((groupId) => {
              const groupEntity = availableUsersGroups.find(
                (ug: any) => ug.id === parseInt(groupId) && ug.systemHelperType === SystemHelperType.GROUP
              )
              if (groupEntity) {
                acls[aclLevel].merged.push(groupEntity as any)
                acls[aclLevel].groups.push(groupEntity as any)
              }
            })
          }
        })
      }

      // Convert to array and return
      return Object.values(acls)
    } catch (error) {
      throw new Error(`Failed to get file ACLs: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Get ACL information for a file (Legacy-compatible version)
   * This version matches the exact behavior of the AngularJS acl.service.js getFsiAcls method.
   *
   * Key differences from getFsiAcls:
   * 1. Uses cloneDeep for availableUsersGroups instead of spread operator
   * 2. Only populates 'merged' array (not separate 'users' and 'groups' arrays)
   * 3. Returns assembledAcl array in same order as legacy
   *
   * @param fsi - File system information
   * @returns Promise that resolves to array of ACL assignments
   */
  public async getFsiAclsLegacy(fsi: FileSystemInfo): Promise<AclAssignment[]> {
    if (!fsi || !fsi.id) {
      throw new Error('FSI ID must be specified')
    }

    try {
      // Get available users and groups
      const availableUsersGroupsResponse = await usersApiService.getAssembledUsersGroups(true)
      const availableUsersGroups = availableUsersGroupsResponse.merged

      // Initialize ACL structure (same as legacy: only merged array, no separate users/groups)
      const acls: Record<FileAclLevel, AclAssignment> = {} as Record<FileAclLevel, AclAssignment>

      this.aclLevels.forEach((level) => {
        acls[level] = {
          level,
          merged: [],
          users: [], // Keep for type compatibility but legacy doesn't populate this
          groups: [], // Keep for type compatibility but legacy doesn't populate this
          // Use JSON parse/stringify for deep clone (equivalent to angular.copy)
          availableUsersGroups: JSON.parse(JSON.stringify(availableUsersGroups)),
          editing: false
        }
      })

      // Get file ACL data
      const fileAclData = await AclApiService.getFileACL(fsi.id)

      // Process user ACLs - only populate merged array like legacy
      if (fileAclData.userAcls) {
        Object.entries(fileAclData.userAcls).forEach(([level, userIds]) => {
          const aclLevel = level as FileAclLevel
          if (acls[aclLevel] && userIds) {
            userIds.forEach((userId) => {
              availableUsersGroups.forEach((userGroup: any) => {
                if (userGroup.id === parseInt(userId) && userGroup.systemHelperType === SystemHelperType.USER) {
                  acls[aclLevel].merged.push(userGroup)
                }
              })
            })
          }
        })
      }

      // Process group ACLs - only populate merged array like legacy
      if (fileAclData.groupAcls) {
        Object.entries(fileAclData.groupAcls).forEach(([level, groupIds]) => {
          const aclLevel = level as FileAclLevel
          if (acls[aclLevel] && groupIds) {
            groupIds.forEach((groupId) => {
              availableUsersGroups.forEach((userGroup: any) => {
                if (userGroup.id === parseInt(groupId) && userGroup.systemHelperType === SystemHelperType.GROUP) {
                  acls[aclLevel].merged.push(userGroup)
                }
              })
            })
          }
        })
      }

      // Build assembled ACL array (same as legacy)
      const assembledAcl: AclAssignment[] = []
      Object.entries(acls).forEach(([key, acl]) => {
        acl.level = key as FileAclLevel
        acl.editing = false
        assembledAcl.push(acl)
      })

      return assembledAcl
    } catch (error) {
      throw new Error(`Failed to get file ACLs: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Validate ACL assignments
   * @param acls - Array of ACL assignments to validate
   * @returns Validation result with errors and warnings
   */
  public validateAcls(acls: AclAssignment[]): AclValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Check for duplicate levels
    const levels = acls.map((acl) => acl.level)
    const duplicateLevels = levels.filter((level, index) => levels.indexOf(level) !== index)
    if (duplicateLevels.length > 0) {
      errors.push(`Duplicate ACL levels found: ${duplicateLevels.join(', ')}`)
    }

    // Check for invalid levels
    acls.forEach((acl) => {
      if (!this.aclLevels.includes(acl.level)) {
        errors.push(`Invalid ACL level: ${acl.level}`)
      }
    })

    // Check for users/groups assigned to multiple levels
    const allAssignments = new Map<number, FileAclLevel[]>()
    acls.forEach((acl) => {
      acl.merged.forEach((entity) => {
        if (!allAssignments.has(entity.id)) {
          allAssignments.set(entity.id, [])
        }
        allAssignments.get(entity.id)!.push(acl.level)
      })
    })

    allAssignments.forEach((assignedLevels, entityId) => {
      if (assignedLevels.length > 1) {
        const entity = acls.flatMap((acl) => acl.merged).find((e) => e.id === entityId)
        const entityName = entity ? entity.name || entity.accountName || `ID: ${entityId}` : `ID: ${entityId}`
        warnings.push(`${entityName} is assigned to multiple ACL levels: ${assignedLevels.join(', ')}`)
      }
    })

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }

  /**
   * Create a new empty ACL assignment for a specific level
   * @param level - The ACL level
   * @param availableUsersGroups - Available users and groups for assignment
   * @returns New ACL assignment
   */
  public createEmptyAclAssignment(level: FileAclLevel, availableUsersGroups: UserGroupEntity[] = []): AclAssignment {
    return {
      level,
      merged: [],
      users: [],
      groups: [],
      availableUsersGroups: [...availableUsersGroups],
      editing: false
    }
  }

  /**
   * Clone an ACL assignment for editing
   * @param acl - The ACL assignment to clone
   * @returns Cloned ACL assignment
   */
  public cloneAclAssignment(acl: AclAssignment): AclAssignment {
    return {
      level: acl.level,
      merged: [...acl.merged],
      users: [...acl.users],
      groups: [...acl.groups],
      availableUsersGroups: [...acl.availableUsersGroups],
      editing: acl.editing
    }
  }

  /**
   * Get users and groups that have access at or above a certain level
   * @param acls - Array of ACL assignments
   * @param minLevel - Minimum ACL level required
   * @returns Array of users and groups with sufficient access
   */
  public getEntitiesWithAccess(acls: AclAssignment[], minLevel: FileAclLevel): UserGroupEntity[] {
    const entitiesWithAccess: UserGroupEntity[] = []

    acls.forEach((acl) => {
      if (this.isAbove(acl.level, minLevel)) {
        entitiesWithAccess.push(...acl.merged)
      }
    })

    // Remove duplicates based on ID and type
    const uniqueEntities = entitiesWithAccess.filter(
      (entity, index, arr) =>
        arr.findIndex((e) => e.id === entity.id && e.systemHelperType === entity.systemHelperType) === index
    )

    return uniqueEntities
  }
}

// Export singleton instance
export const aclService = AclService.getInstance()

export default aclService
